import * as React from "react";
import { lightTheme } from "./layout/Theme";
import { Route } from "react-router-dom";
import { Admin, CustomRoutes, mergeTranslations, Notification } from "react-admin";
import { PageList, PageEdit, PageCreate } from "./resources/page";
import { ArticleList, ArticleEdit, ArticleCreate } from "./resources/article";
import { UserList, UserEdit, UserCreate } from "./resources/user";
import { QueryClient } from 'react-query';
import { TagList, TagEdit, TagCreate } from "./resources/tag";
import {
  ProductList,
  ProductCreate,
  ProductEdit,
  AttributeSetList,
  AttributeSetEdit,
  AttributeSetCreate,
  PriceCalculatorList,
  PriceCalculatorEdit,
  PriceCalculatorCreate,
  AttributeList,
  AttributeEdit,
  AttributeCreate,
  CategoryList as ProductCategoryList,
  StickerList,
  StickerCreate,
  StickerEdit,
} from "./resources/product";
import {
  CategoryEdit,
  CategoryCreate,
  CategoryList,
} from "./resources/category";
import {
  CategoryEdit as FaqCategoryEdit,
  CategoryCreate as FaqCategoryCreate,
  CategoryList as FaqCategoryList,
} from "./resources/faq/category";
import {
  SheetList,
  SheetEdit,
  SheetCreate,
  ImportProcess,
} from "./resources/importer";

import {
  PromotionList,
  PromotionEdit,
  PromotionCreate,
} from "./resources/promotion";

import {
  NotificationList,
  NotificationEdit,
  NotificationCreate,
} from "./resources/notification";

import {
  NewsletterList,
  NewsletterEdit,
  NewsletterCreate,
} from "./resources/newsletter";

import Dashboard from "./layout/Dashboard";
import { MainLayout } from "./layout/Layout";
import AuthProvider from "./providers/AuthProvider";
import ApiProvider from "./providers/ApiProvider";
import HttpClient from "./providers/HttpClient";

import { OrderList, OrderShow } from "./resources/sales";
import {
  TaxClassList,
  TaxClassEdit,
  TaxClassCreate,
} from "./resources/tax/tax-class";
import { StockList, StockEdit, StockCreate } from "./resources/stock";
import { TaxRatesList } from "./resources/tax/tax-rates/TaxRatesList";
import { TaxRatesCreate, TaxRatesEdit } from "./resources/tax/tax-rates";
import { FaqCreate, FaqEdit, FaqList } from "./resources/faq";

import {
  NavigationCreate,
  NavigationEdit,
  NavigationItemsList,
  NavigationList,
} from "./resources/navigation";
import {
  addTreeMethodsBasedOnParentAndPosition,
  raTreeLanguageEnglish,
} from "src/enterprise/ra-tree";
import addSearchMethod from "./providers/addSearchMethod";
import { Resource } from "src/enterprise/ra-rbac";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import customEnglishMessages from "src/i18n/messages-english";
import { Configuration } from "./resources/configuration";
import { LoginPage } from './layout/LoginPage';

const i18nProvider = polyglotI18nProvider((locale) => {
  return mergeTranslations(englishMessages, raTreeLanguageEnglish, customEnglishMessages);
}, "en");

const dataProvider = ApiProvider(
  process.env.REACT_APP_API_BASE_URL,
  HttpClient
);
const dataProviderWithTree =
  addTreeMethodsBasedOnParentAndPosition(dataProvider);
const dataProviderAndSearch = addSearchMethod(dataProviderWithTree);

const queryClient = new QueryClient({
  defaultOptions: {
      queries: {
          staleTime: 300000,
          retry: false,
          refetchOnWindowFocus: true,
          refetchOnMount: "always"
      },
  },
});

const CustomNotification = () => <Notification autoHideDuration={3000} />;

const App = () => (
  <React.Fragment>
    <Admin
      requireAuth
      layout={MainLayout}
      dataProvider={dataProviderAndSearch}
      authProvider={AuthProvider}
      dashboard={Dashboard}
      i18nProvider={i18nProvider}
      theme={lightTheme}
      queryClient={queryClient}
      notification={CustomNotification}
      loginPage={LoginPage}
    >
      <Resource
        name="admin/pages"
        list={PageList}
        edit={PageEdit}
        create={PageCreate}
      />
      <Resource
        name="admin/articles"
        list={ArticleList}
        edit={ArticleEdit}
        create={ArticleCreate}
      />
      <Resource
        name="admin/article/tags"
        list={TagList}
        edit={TagEdit}
        create={TagCreate}
      />
      <Resource
        name="admin/article/categories"
        list={CategoryList}
        edit={CategoryEdit}
        create={CategoryCreate}
      />
      <Resource name="admin/users" list={UserList} edit={UserEdit} create={UserCreate} />

      <Resource
        name="admin/promotions"
        list={PromotionList}
        edit={PromotionEdit}
        create={PromotionCreate}
      />
      <Resource
        name="admin/notifications"
        list={NotificationList}
        edit={NotificationEdit}
        create={NotificationCreate}
      />
      <Resource
        name="admin/products"
        list={ProductList}
        edit={ProductEdit}
        create={ProductCreate}
      />
      <Resource
        name="admin/product/attribute-sets"
        list={AttributeSetList}
        edit={AttributeSetEdit}
        create={AttributeSetCreate}
      />
      <Resource
        name="admin/product/price-calculators"
        list={PriceCalculatorList}
        edit={PriceCalculatorEdit}
        create={PriceCalculatorCreate}
      />
      <Resource
        name="admin/product/attributes"
        list={AttributeList}
        edit={AttributeEdit}
        create={AttributeCreate}
      />
      <Resource
        name="admin/product/stickers"
        list={StickerList}
        edit={StickerEdit}
        create={StickerCreate}
      />
      <Resource
        name="admin/product/categories"
        list={ProductCategoryList}
      />

      <Resource
        name="admin/importer/sheets"
        list={SheetList}
        edit={SheetEdit}
        create={SheetCreate}
      />
      <Resource
        name="admin/taxes"
        list={TaxClassList}
        edit={TaxClassEdit}
        create={TaxClassCreate}
      />
      <Resource
        name="admin/faqs"
        list={FaqList}
        edit={FaqEdit}
        create={FaqCreate}
      />
      <Resource
        name="admin/faq/categories"
        list={FaqCategoryList}
        edit={FaqCategoryEdit}
        create={FaqCategoryCreate}
      />
      <Resource
        name="admin/navigations"
        list={NavigationList}
        edit={NavigationEdit}
        create={NavigationCreate}
      />
      <Resource
        name="admin/navigations/:navigationId/items"
        list={NavigationItemsList}
      />
      <Resource
        name="admin/product/stock"
        list={StockList}
        edit={StockEdit}
        create={StockCreate}
      />
      <Resource
        name="admin/newsletter/subscribers"
        list={NewsletterList}
        edit={NewsletterEdit}
        create={NewsletterCreate}
      />
      <Resource name="admin/orders" list={OrderList} show={OrderShow} />
      <Resource
        name="admin/tax/items"
        list={TaxRatesList}
        edit={TaxRatesEdit}
        create={TaxRatesCreate}
      />
      <CustomRoutes>
        <Route path="admin/importer" element={<ImportProcess />} />
        <Route path="admin/configurations" element={<Configuration />} />
      </CustomRoutes>
    </Admin>
  </React.Fragment>
);
export default App;
